import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntForm, Input, InputNumber, Modal, Switch } from 'antd';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import type { Translator } from '@/i18n/config';
import type { Language, LanguageCreateInput, LanguageUpdateInput } from '../types';

const FORM_ID = 'language-form';
const CODE_RULE = /^[a-z]{2,10}(-[a-z]{2,10})?$/;

function makeSchema(t: Translator) {
  return z.object({
    code: z.string().regex(CODE_RULE, t('form.codeInvalid')),
    name: z.string().min(1, t('form.nameRequired')).max(80),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    displayOrder: z.number().int().min(0).max(999),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

interface LanguageFormModalProps {
  open: boolean;
  editing: Language | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: LanguageCreateInput) => void;
  onUpdate: (id: string, input: LanguageUpdateInput) => void;
}

export function LanguageFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: LanguageFormModalProps) {
  const { t } = useTranslation(['languages', 'common', 'validation']);
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const isEdit = editing !== null;

  const defaultValues: Values = {
    code: editing?.code ?? '',
    name: editing?.name ?? '',
    isDefault: editing?.isDefault ?? false,
    isActive: editing?.isActive ?? true,
    displayOrder: editing?.displayOrder ?? 0,
  };

  const onSubmit = (values: Values) => {
    if (isEdit && editing) {
      onUpdate(editing.id, {
        name: values.name.trim(),
        isDefault: values.isDefault,
        isActive: values.isActive,
        displayOrder: values.displayOrder,
        rowVersion: editing.rowVersion,
      });
    } else {
      onCreate({
        code: values.code.trim(),
        name: values.name.trim(),
        isDefault: values.isDefault,
        isActive: values.isActive,
        displayOrder: values.displayOrder,
      });
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      onCancel={onCancel}
      okText={isEdit ? t('form.save') : t('form.create')}
      cancelText={t('common:actions.cancel')}
      okButtonProps={{ htmlType: 'submit', form: FORM_ID, loading: saving }}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form
        id={FORM_ID}
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        options={{ values: defaultValues }}
      >
        {({ watch, setValue }) => {
          const isDefault = watch('isDefault');
          const isActive = watch('isActive');
          const displayOrder = watch('displayOrder');
          return (
            <AntForm layout="vertical" component="div">
              <FormField name="code" label={t('form.code')} required help={t('form.codeHint')}>
                <Input placeholder={t('form.codePlaceholder')} disabled={isEdit} maxLength={10} />
              </FormField>
              <FormField name="name" label={t('form.name')} required>
                <Input placeholder={t('form.namePlaceholder')} maxLength={80} />
              </FormField>
              <AntForm.Item label={t('form.isDefault')} extra={t('form.isDefaultHint')}>
                <Switch
                  checked={isDefault}
                  onChange={(v) => {
                    setValue('isDefault', v, { shouldDirty: true });
                    // Varsayılan dil pasif olamaz (BE LANGUAGE_DEFAULT_IMMUTABLE) —
                    // switch disable edilirken değer de aktife çekilmeli.
                    if (v) setValue('isActive', true, { shouldDirty: true });
                  }}
                />
              </AntForm.Item>
              <AntForm.Item label={t('form.isActive')}>
                <Switch
                  checked={isActive}
                  // Varsayılan dil pasifleştirilemez (BE LANGUAGE_DEFAULT_IMMUTABLE).
                  disabled={isDefault}
                  onChange={(v) => setValue('isActive', v, { shouldDirty: true })}
                />
              </AntForm.Item>
              <AntForm.Item label={t('form.displayOrder')}>
                <InputNumber
                  min={0}
                  max={999}
                  value={displayOrder}
                  onChange={(v) =>
                    setValue('displayOrder', typeof v === 'number' ? v : 0, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  style={{ width: '100%' }}
                />
              </AntForm.Item>
            </AntForm>
          );
        }}
      </Form>
    </Modal>
  );
}
